import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { SiteTabs } from "@/components/SiteTabs";
import { UnitCard } from "@/components/UnitCard";
import { UnitDrawer } from "@/components/UnitDrawer";
import { Input } from "@/components/ui/input";
import { ViewNav } from "@/components/ViewNav";
import { MoveInDialog } from "@/components/MoveInDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Loader2, FileDown, X } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { downloadCustomerList } from "@/lib/pdfExport";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { user } = useAuth();
  const restrictedKey = user && user.site_access && user.site_access !== "all" ? user.site_access.toUpperCase() : null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [units, setUnits] = useState([]);
  const [stats, setStats] = useState(null);
  const [site, setSite] = useState(restrictedKey || searchParams.get("site") || "ALL");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [search, setSearch] = useState("");
  const [sizeFilter, setSizeFilter] = useState(searchParams.get("size") || "");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState("details");
  const [exporting, setExporting] = useState(false);

  const loadUnits = useCallback(async (s = site, st = status) => {
    setLoading(true);
    try {
      const data = await api.getUnits({ site: s, status: st, size: sizeFilter || undefined });
      setUnits(data);
    } finally {
      setLoading(false);
    }
  }, [site, status, sizeFilter]);
  const loadStats = useCallback(async () => setStats(await api.getStats()), []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadUnits(site, status); }, [loadUnits, site, status]);

  const clearSizeFilter = () => {
    setSizeFilter("");
    const next = new URLSearchParams(searchParams);
    next.delete("size");
    setSearchParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return units;
    const q = search.toLowerCase();
    return units.filter(
      (u) =>
        u.unit_number.toLowerCase().includes(q) ||
        (u.tenant_name || "").toLowerCase().includes(q) ||
        (u.phone || "").toLowerCase().includes(q)
    );
  }, [units, search]);

  const openUnit = (u) => { setDrawerTab("details"); setSelected(u); setDrawerOpen(true); };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const targets =
        site === "ALL"
          ? (stats?.sites || []).map((s) => ({ key: s.key, name: s.name }))
          : [{ key: site, name: (stats?.sites || []).find((s) => s.key === site)?.name || site }];
      if (targets.length === 0) {
        toast.error("No sites available to export");
        return;
      }
      const groups = await Promise.all(
        targets.map(async (t) => ({
          name: t.name,
          units: await api.getUnits({ site: t.key, status: "all" }),
        }))
      );
      const total = groups.reduce((a, g) => a + g.units.length, 0);
      if (total === 0) {
        toast.error("No sheds to export for this selection");
        return;
      }
      downloadCustomerList({
        fileLabel: site === "ALL" ? "All-Sites" : (targets[0].name || site).replace(/\s+/g, "-"),
        groups,
      });
      toast.success(`Exported ${total} shed${total === 1 ? "" : "s"} to PDF`);
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleDeleted = (id) => {
    setUnits((prev) => prev.filter((u) => u.id !== id));
    setDrawerOpen(false);
    loadStats();
  };

  const handleSaved = (updated) => {
    if (updated) {
      setUnits((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      setSelected(updated);
    }
    loadStats();
    if (updated && status !== "all" && updated.status !== status) {
      setUnits((prev) => prev.filter((u) => u.id !== updated.id));
      setDrawerOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 w-full max-w-full overflow-x-hidden">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm w-full max-w-full overflow-hidden">
        <div className="max-w-[1500px] mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <Brand subtitle={stats ? `${stats.total} units · ${stats.sites.length} site${stats.sites.length === 1 ? "" : "s"}` : "Loading..."} />
          <div className="flex items-center gap-4">
            <ViewNav />
            <div className="relative w-full max-w-xs hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="unit-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search unit, tenant, phone..."
                className="pl-9 h-9 bg-slate-50"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 py-6 space-y-6 w-full max-w-full overflow-hidden">
        <div className="flex flex-col gap-3 w-full max-w-full overflow-hidden">
          {stats && (
            <div className="w-full max-w-full overflow-x-auto pb-1">
              <SiteTabs active={site} onChange={setSite} sites={stats.sites} total={stats.total} restrictedKey={restrictedKey} />
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap w-full">
            <div className="relative w-full sm:hidden">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..." className="pl-9 h-9" />
            </div>
            <MoveInDialog sites={stats?.sites || []} restrictedKey={restrictedKey} onDone={() => { loadUnits(site, status); loadStats(); }} />
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-[180px] h-9 bg-white" data-testid="status-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="available">Available (vacant / cleaning / maint.)</SelectItem>
                <SelectItem value="occupied">Occupied</SelectItem>
                <SelectItem value="vacant">Vacant</SelectItem>
                <SelectItem value="maintenance">Maintenance</SelectItem>
                <SelectItem value="needs_cleaning">Needs Cleaning</SelectItem>
                <SelectItem value="management">Management / Internal</SelectItem>
                <SelectItem value="reserved">Reserved</SelectItem>
              </SelectContent>
            </Select>
            {sizeFilter && (
              <button
                type="button"
                data-testid="size-filter-chip"
                onClick={clearSizeFilter}
                className="inline-flex items-center gap-1.5 h-9 rounded-md border border-blue-300 bg-blue-50 px-3 text-sm font-medium text-blue-700 hover:bg-blue-100 transition-colors"
              >
                Size: {sizeFilter === "unspecified" ? "No size" : sizeFilter}
                <X className="h-3.5 w-3.5" />
              </button>
            )}
            <Button
              data-testid="export-customer-pdf-btn"
              onClick={handleExportPdf}
              disabled={exporting}
              variant="outline"
              className="h-9 gap-2 border-slate-300 bg-white hover:bg-slate-50"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
              {site === "ALL" ? "Download Customer List (All)" : "Download Customer List"}
            </Button>
            <p className="text-sm text-slate-500 ml-auto">
              <span className="font-semibold text-slate-900 font-mono-nums">{filtered.length}</span> units
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading units...
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400">No units match your filters.</div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 w-full"
            data-testid="unit-grid"
          >
            {filtered.map((u) => (
              <UnitCard key={u.id} unit={u} onClick={openUnit} />
            ))}
          </div>
        )}
      </main>

      <UnitDrawer
        unit={selected}
        open={drawerOpen}
        initialTab={drawerTab}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </div>
  );
}